import { resolvePortalMessage } from "../../../i18n/message.js";
import { portalLocale, portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `Organization` → Finance → Postpaid — `/finance/postpaid` (plan-014 §4, design §27.15.1; FR12-PST-01…05). Built by
 * plan-014 unit **E5**.
 *
 * Drawn to the prototype's `postpaid` page (`design/WeTalk.dc.html`, the Organization block renderer): the organization
 * and the contract's date as kicker, "Postpaid", the lede, the Finance tab strip, then the blocks — the four stats
 * (Credit limit "approved by WeTalk", Owed now "billed + not billed yet", Still available "new calls stop at zero",
 * Terms "14 days"), the "If a payment fails" table (drawn from the contract's own schedule — due date, +7, +14 — not
 * the prototype's illustrative days), and a notice: the invoice to pay, with "Pay by card" (the processor's hosted
 * page), or the month settled. Styles are the shared Organization renderer's (`../../account-admin/page/
 * organization-page.css`) plus `./postpaid-screen.css`, tokens only (ADR 0018).
 *
 * EVERY STATE IS REAL (FR12-SCR-04): loading; a failure in the API's words; no organization open; no finance role; a
 * balance that pays in advance ("Postpaid is a contract WeTalk approves"); an invoice due, overdue, being paid; new paid
 * calls stopped for nonpayment (and what restores them: paying — paused campaigns stay paused); the month settled.
 * The server decides everything; this page only shows what it answered.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { money, type CurrencyCode } from "@wetalk/domain";
import { Button, Icon, Select } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import type { BillingAccountSummaryView } from "../../../api/slice/billing-account.js";
import type { PostpaidActionView, PostpaidInvoiceView, PostpaidView } from "../../../api/slice/postpaid.js";
import { formatMoney } from "../../../money.js";
import { GLYPH } from "../../../shell/glyph.js";
import { roleHolds, useOpenAccount } from "../../account-admin/overview/account-overview-screen.js";
import { Block, BlockNote, Notice, StatGrid, type Outcome } from "../../account-admin/page/organization-page.js";

import "./postpaid-screen.css";
import { FinanceTabs } from "../finance-tabs.js";

/** The page's search parameters: the postpaid balance shown (the address bar is untrusted). */
export interface PostpaidSearch {
  readonly balance?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** The route's `validateSearch`: a total function, so a hand-edited URL still opens a valid page. */
export function postpaidSearchOf(raw: Readonly<Record<string, unknown>>): PostpaidSearch {
  const balance = raw["balance"];
  return typeof balance === "string" && UUID.test(balance) ? { balance } : {};
}

export interface PostpaidScreenProps {
  readonly search: PostpaidSearch;
  readonly onSearch: (next: PostpaidSearch) => void;
}

// ── copy ──────────────────────────────────────────────────────────────────────────────────────────────────────

export const POSTPAID_COPY = {
  get kicker() { return portalText("finance", "m0515"); },
  get title() { return portalText("finance", "m1234"); },
  get lede() { return portalText("finance", "m1247"); },
  get scheduleTitle() { return portalText("finance", "m1248"); },
  get scheduleDek() { return portalText("finance", "m1249"); },
  get noContractTitle() { return portalText("finance", "m1250"); },
  get noContract() { return portalText("finance", "m1251"); },
  get noAccess() { return portalText("finance", "m1252"); },
  get chooseOrganization() { return portalText("finance", "m1224"); },
  get suspendedTitle() { return portalText("finance", "m1253"); },
  get suspended() { return portalText("finance", "m1254"); },
} as const;

const MONTH = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] as const;

/** `October` for `2026-10`. */
export function monthWordOf(yearMonth: string): string {
  if (portalLocale() === "el" && /^\d{4}-\d{2}$/.test(yearMonth)) return new Intl.DateTimeFormat("el-GR", { month: "long", timeZone: "UTC" }).format(new Date(`${yearMonth}-01T12:00:00Z`));
  return MONTH[Number(yearMonth.slice(5, 7)) - 1] ?? yearMonth;
}

/** `15 Nov` in `timeZone`. */
export function dayWordOf(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-GB"), { timeZone, day: "numeric", month: "short" }).format(new Date(iso));
}

function moneyOf(minor: number, currency: string): string {
  const text = formatMoney(money(Math.abs(minor), currency as CurrencyCode));
  return minor < 0 ? `−${text}` : text;
}

/** "collected by card", "by card or bank transfer", "by bank transfer". */
export function collectedByOf(method: readonly string[]): string {
  const card = method.includes("card_payment_link") || method.includes("mandate");
  const bank = method.includes("bank_transfer");
  if (card && bank) return portalText("finance", "m0725");
  return card ? portalText("finance", "m0726") : portalText("finance", "m0727");
}

/** What one step of the schedule does, in the person's words. */
export function stepWordOf(action: readonly PostpaidActionView[]): string {
  if (action.includes("suspend")) return portalText("finance", "m0728");
  const part: string[] = [];
  if (action.includes("charge")) part.push(portalText("finance", "m0729"));
  if (action.includes("reminder")) part.push(action.includes("charge") ? portalText("finance", "m0730") : portalText("finance", "m0731"));
  if (action.includes("banner")) part.push(portalText("finance", "m0732"));
  if (part.length === 1) return part[0] as string;
  return portalText("finance", "m0733", { value1: part.slice(0, -1).join(", "), value2: part[part.length - 1] as string });
}

/** "Due date", "+7 days". */
export function stepWhenOf(day: number): string {
  if (day === 0) return portalText("finance", "m0734");
  return portalText("finance", "postpaid.overdue", { count: day });
}

// ── the page ──────────────────────────────────────────────────────────────────────────────────────────────────

function FinanceHeader(props: { readonly outcome: Outcome | null }): ReactNode {
  usePortalTranslation("finance");
  return (
    <>
      <h1 className="op-title">{POSTPAID_COPY.title}</h1>
      <p className="op-lede">{POSTPAID_COPY.lede}</p>
      <FinanceTabs current="postpaid" />
      {props.outcome === null ? null : (
        <p className={props.outcome.tone === "done" ? "op-toast" : "op-toast op-toast-problem"} role="status">
          <Icon glyph={props.outcome.tone === "done" ? GLYPH.checkCircle : GLYPH.warningCircle} size="lg" />
          <span>{resolvePortalMessage(props.outcome.text)}</span>
        </p>
      )}
    </>
  );
}

function ScheduleTable(props: { readonly view: PostpaidView }): ReactNode {
  usePortalTranslation("finance");
  const schedule = props.view.contract?.schedule ?? [];
  return (
    <div className="op-table-wrap">
      <table className="table op-table pp-table">
        <thead>
          <tr>
            <th>{portalText("finance", "m0736")}</th>
            <th>{portalText("finance", "m0737")}</th>
          </tr>
        </thead>
        <tbody>
          {schedule.map((one) => (
            <tr key={one.day}>
              <td className="pp-cell pp-when">{stepWhenOf(one.day)}</td>
              <td className="pp-cell">{stepWordOf(one.action)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvoiceNotice(props: {
  readonly invoice: PostpaidInvoiceView;
  readonly view: PostpaidView;
  readonly canPay: boolean;
  readonly busy: boolean;
  readonly onPay: (invoice: PostpaidInvoiceView) => void;
}): ReactNode {
  usePortalTranslation("finance");
  const { invoice, view } = props;
  const zone = view.billingTimeZone;
  const gross = moneyOf(invoice.grossDueMinor, invoice.currency);
  const month = monthWordOf(invoice.yearMonth);
  const card = view.contract === null || view.contract.collectionMethod.some((one) => one !== "bank_transfer");
  const title = invoice.overdue ? portalText("finance", "m0738", { value1: month, value2: gross }) : portalText("finance", "m0739", { value1: month, value2: gross, value3: dayWordOf(invoice.dueAt, zone) });
  const text = invoice.paymentPending
    ? portalText("finance", "m0740")
    : portalText("finance", "m0741", { value1: moneyOf(invoice.netDueMinor, invoice.currency), value2: moneyOf(invoice.taxMinor, invoice.currency), value3: month, value4: card ? portalText("finance", "m1179") : portalText("finance", "m1180") });
  return (
    <Notice
      tone={invoice.overdue ? "accent" : "neutral"}
      glyph={invoice.overdue ? GLYPH.warningCircle : GLYPH.fileText}
      title={title}
      text={text}
      actions={
        props.canPay && card && !invoice.paymentPending ? (
          <Button variant="primary" disabled={props.busy} onClick={() => props.onPay(invoice)}>{portalText("finance", "m0742")}</Button>
        ) : undefined
      }
    />
  );
}

function PostpaidBody(props: {
  readonly view: PostpaidView;
  readonly canPay: boolean;
  readonly busy: boolean;
  readonly onPay: (invoice: PostpaidInvoiceView) => void;
}): ReactNode {
  usePortalTranslation("finance");
  const { view } = props;
  const contract = view.contract;
  if (contract === null) {
    return (
      <Block>
        <Notice tone="neutral" glyph={GLYPH.creditCard} title={POSTPAID_COPY.noContractTitle} text={POSTPAID_COPY.noContract} />
      </Block>
    );
  }
  const currency = view.currency;
  const settled = view.lastSettled;
  return (
    <>
      <Block>
        <StatGrid
          stat={[
            { label: portalText("finance", "m0743"), value: moneyOf(contract.creditLimitMinor, currency), note: portalText("finance", "m0744") },
            {
              label: portalText("finance", "m0745"),
              value: moneyOf(view.owedMinor.total, currency),
              note: portalText("finance", "m0746", { value1: moneyOf(view.owedMinor.billed, currency), value2: moneyOf(view.owedMinor.notBilled, currency) }),
            },
            { label: portalText("finance", "m0747"), value: moneyOf(view.stillAvailableMinor, currency), note: portalText("finance", "m0748") },
            { label: portalText("finance", "m0749"), value: portalText("finance", "postpaid.days", { count: contract.paymentTermDay }), note: collectedByOf(contract.collectionMethod) },
          ]}
        />
      </Block>
      {view.collectionState === "suspended_nonpayment" ? (
        <Block>
          <Notice tone="accent" glyph={GLYPH.warningCircle} title={POSTPAID_COPY.suspendedTitle} text={POSTPAID_COPY.suspended} />
        </Block>
      ) : null}
      <Block title={POSTPAID_COPY.scheduleTitle} dek={POSTPAID_COPY.scheduleDek}>
        <ScheduleTable view={view} />
      </Block>
      {view.openInvoice.length > 0 ? (
        <Block title={view.openInvoice.length === 1 ? portalText("finance", "m0750") : portalText("finance", "m0751", { value1: String(view.openInvoice.length) })} dek={portalText("finance", "m0752")}>
          <div className="pp-invoices">
            {view.openInvoice.map((one) => (
              <InvoiceNotice key={one.billingPeriodCloseId} invoice={one} view={view} canPay={props.canPay} busy={props.busy} onPay={props.onPay} />
            ))}
          </div>
        </Block>
      ) : settled !== null ? (
        <Block>
          <Notice
            tone="accent"
            glyph={GLYPH.checkCircle}
            title={portalText("finance", "m0753", { value1: monthWordOf(settled.yearMonth) })}
            text={
              portalText("finance", "m0754", { value1: settled.settledBy === "bank_transfer" ? portalText("finance", "m1181") : portalText("finance", "m1182"), value2: dayWordOf(settled.settledAt ?? settled.dueAt, view.billingTimeZone), value3: dayWordOf(new Date(Date.parse(view.nextCloseAt) - 60_000).toISOString(), view.billingTimeZone), value4: view.billingTimeZone })
            }
          />
        </Block>
      ) : (
        <Block>
          <Notice
            tone="neutral"
            glyph={GLYPH.calendarCheck}
            title={portalText("finance", "m0755")}
            text={portalText("finance", "m0756", { value1: dayWordOf(new Date(Date.parse(view.nextCloseAt) - 60_000).toISOString(), view.billingTimeZone), value2: view.billingTimeZone, value3: String(contract.paymentTermDay) })}
          />
        </Block>
      )}
    </>
  );
}

export function PostpaidScreen(props: PostpaidScreenProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const open = useOpenAccount();
  const accountId = open.accountId;
  const canRead = roleHolds(open.accountRole, "account.finance.read");
  const canManage = roleHolds(open.accountRole, "account.finance.manage");
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const balances = useQuery({
    queryKey: ["postpaid", "balances", accountId ?? "none"],
    queryFn: () => gateway.listBillingAccounts(accountId ?? ""),
    enabled: accountId !== null && canRead,
  });
  const postpaidBalances: readonly BillingAccountSummaryView[] = (balances.data ?? []).filter((one) => one.collectionPolicy === "postpaid");
  const shown = postpaidBalances.find((one) => one.billingAccountId === props.search.balance) ?? postpaidBalances[0] ?? (balances.data ?? [])[0] ?? null;
  const shownId = shown === null ? null : shown.billingAccountId;
  const postpaid = useQuery({
    queryKey: ["postpaid", "view", shownId ?? "none"],
    queryFn: () => gateway.readPostpaid(shownId ?? ""),
    enabled: shownId !== null && canRead,
  });
  const pay = useMutation({
    mutationFn: (invoice: PostpaidInvoiceView) => gateway.payPostpaid(shownId ?? "", invoice.billingPeriodCloseId),
    onSuccess: async (payment) => {
      await queryClient.invalidateQueries({ queryKey: ["postpaid", "view"] });
      const url = payment.nextActionUrl ?? payment.payUrl;
      if (url === null) {
        setOutcome({ tone: "done", text: () => (portalText("finance", "m0757")) });
        return;
      }
      setOutcome({ tone: "done", text: () => (portalText("finance", "m0758")) });
      window.location.assign(url);
    },
    onError: (error) => setOutcome({ tone: "problem", text: () => (apiProblemText(error, portalText("finance", "m0759"))) }),
  });


  if (accountId === null) {
    return (
      <section className="op-page">
        <FinanceHeader outcome={null} />
        <Block>
          <BlockNote>{open.loading ? portalText("finance", "m0454") : POSTPAID_COPY.chooseOrganization}</BlockNote>
        </Block>
      </section>
    );
  }

  return (
    <section className="op-page">
      <FinanceHeader outcome={outcome} />
      {!canRead ? (
        <Block>
          <BlockNote>{POSTPAID_COPY.noAccess}</BlockNote>
        </Block>
      ) : balances.isPending || (shownId !== null && postpaid.isPending) ? (
        <Block>
          <BlockNote>{portalText("finance", "m0760")}</BlockNote>
        </Block>
      ) : balances.isError ? (
        <Block>
          <BlockNote>{apiProblemText(balances.error, portalText("finance", "m0761"))}</BlockNote>
        </Block>
      ) : postpaid.isError ? (
        <Block>
          <BlockNote>{apiProblemText(postpaid.error, portalText("finance", "m0762"))}</BlockNote>
        </Block>
      ) : postpaid.data === undefined ? (
        <Block>
          <Notice tone="neutral" glyph={GLYPH.creditCard} title={POSTPAID_COPY.noContractTitle} text={POSTPAID_COPY.noContract} />
        </Block>
      ) : (
        <>
          {postpaidBalances.length > 1 ? (
            <Block
              title={portalText("finance", "m0298")}
              buttons={
                <Select
                  aria-label={portalText("finance", "m0298")}
                  value={shownId ?? ""}
                  onChange={(event) => {
                    setOutcome(null);
                    props.onSearch({ balance: event.target.value });
                  }}
                >
                  {postpaidBalances.map((one) => (
                    <option key={one.billingAccountId} value={one.billingAccountId}>
                      {one.displayName ?? portalText("finance", "m0298")}
                    </option>
                  ))}
                </Select>
              }
            />
          ) : null}
          <PostpaidBody view={postpaid.data} canPay={canManage} busy={pay.isPending} onPay={(invoice) => pay.mutate(invoice)} />
        </>
      )}
    </section>
  );
}
